import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Square } from "lucide-react";
import type {
  GroomingBatchItemStatus,
  GroomingBatchItemView,
  GroomingQueueState,
  GroomingRunnerEvent,
  GroomingStopReason,
} from "@ttc-spectree/toolbox-contract";
import { AgendaView } from "@/components/grooming/agenda-view";
import { applyRunnerEventToCache, isBatchStateEvent } from "@/components/grooming/batch-state";
import { BriefReview, CopyButton, FailedItemCard, NotReadyCard } from "@/components/grooming/brief-review";
import { Chip, Segmented, type ChipTone } from "@/components/grooming/grooming-chips";
import { PostAllDialog, heldBriefItems } from "@/components/grooming/post-all-dialog";
import { QueueBadge, activeEntryByKey, safeQueueKey } from "@/components/grooming/queue-status-badge";
import { RunActivity, useRunProgress, useSupportsRunProgress, type RunProgress } from "@/components/grooming/run-activity";
import { RunnerFallback } from "@/components/grooming/runner-fallback";
import { useBatchRun } from "@/components/grooming/use-batch-run";
import { Button } from "@/components/ui/button";
import { refreshCachedBacklog, useBacklogBriefs } from "@/hooks/queries/use-brief-staleness";
import { groomingKeys } from "@/hooks/queries/use-grooming";
import { canOn, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import type { GroomingBatch } from "@/lib/api/grooming";
import { adminDetailsText } from "@/lib/grooming/copy-details";
import { groomingErrorMessage, groomingFixPath, stopReasonMessage } from "@/lib/grooming/error-messages";
import { formatAboutTime, localTimeOf } from "@/lib/grooming/local-time";
import { LEFT_OUT_TEXT, batchItemBriefState, briefStateTag } from "@/lib/grooming/plain-words";
import { useRunnerBridge, useRunnerEvents } from "@/lib/grooming/runner-bridge";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { notifyBriefsReady } from "@/lib/toolbox/notify";
import { cn } from "@/lib/utils";

/**
 * The run step of a session (epic PI-SPEC-SW-E280, feature PI-SPEC-SW-1403;
 * Claude Design "SpecTree Grooming Desktop", Session run step; was E279's run
 * workspace): a header with the session's name, progress, the Workspace |
 * Agenda switch, Stop and Post all; below it either the Workspace (the run's
 * items on the left with live status, the selected item on the right to
 * review and post while the rest keep running) or the Agenda view.
 *
 * The batch comes from one source, the React Query cache entry for the batch
 * (see `batch-state.ts`). In SpecTree Toolbox each runner event is folded into
 * that entry and the batch is read again; a stop or finish waits for that
 * read before the page treats the run as settled. A browser has no runner
 * events and polls the batch instead (server-sent events are process-local
 * across API replicas). E279's safety re-read one second after a finish or
 * stop (`SETTLE_REFRESH_MS`) is gone: the replay test covers every ordering
 * of events and reads without it.
 *
 * Starting, stopping and resuming a run need `prepare-sessions` on the
 * project and Post all needs `manage-agenda` (Admin, Project Manager; epic
 * PI-SPEC-SW-E283); anyone who can see the run reads it.
 *
 * In a contract-3 app (epic PI-SPEC-SW-E284) Start, Resume and the build
 * step's "Prepare briefs" add the batch to the app's run queue
 * (`enqueueBatch`), each row still waiting shows its queue badge
 * ("Queued · #3", "Running"), and Stop stops this batch's running entries
 * and takes its waiting ones off the queue (other sessions' runs go on). A
 * contract-2 app keeps `runBatch` and `stopBatch`.
 */

/** Item statuses that mean the run is done with the item. */
const FINISHED: ReadonlySet<GroomingBatchItemStatus> = new Set(["briefed", "not-ready", "failed", "skipped", "current"]);

/** What the page heard about a stop beyond what SpecTree stores (the engine's code, message and reset time). */
export interface StopInfo {
  reason: GroomingStopReason;
  errorCode?: string;
  message?: string;
  resumeAt?: string;
  /** When the run stopped (or the page learned of it). */
  at: string;
}


/**
 * The run step's title is the meeting (PKG-PM-C, frame C2, UX-PM-09's
 * vocabulary): `2026-10-26` → "Meeting of Monday, Oct 26"; no date → "This
 * meeting's briefs".
 */
export function meetingTitle(sessionDate: string | null | undefined): string {
  const match = sessionDate ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(sessionDate) : null;
  if (!match) return "This meeting's briefs";
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return "This meeting's briefs";
  return `Meeting of ${date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}`;
}

/** The line under the meeting title: "8 briefs posted", "1 brief posted"; null while none are posted. */
export function postedText(batch: Pick<GroomingBatch, "items">): string | null {
  const posted = batch.items.filter((item) => item.reviewState === "posted").length;
  if (posted === 0) return null;
  return `${String(posted)} ${posted === 1 ? "brief" : "briefs"} posted`;
}

/** The run line under the header: done count and the item being analysed, or how the run ended. */
export function runText(batch: Pick<GroomingBatch, "status" | "items">): string {
  const total = batch.items.length;
  const done = batch.items.filter((i) => FINISHED.has(i.status)).length;
  const briefed = batch.items.filter((i) => i.status === "briefed" || i.status === "current").length;
  const analysing = batch.items.find((i) => i.status === "running");
  switch (batch.status) {
    case "running":
      return analysing ? `${String(done)} of ${String(total)} done · analysing ${analysing.itemId}` : `${String(done)} of ${String(total)} done`;
    case "stopped":
      return `${String(briefed)} of ${String(total)} have a brief · stopped`;
    case "completed":
      return briefed === total && total > 0 ? `All ${String(total)} have a brief` : `${String(done)} of ${String(total)} done · finished`;
    case "canceled":
      return `${String(done)} of ${String(total)} done · canceled`;
    case "draft":
      return `${String(done)} of ${String(total)} done · not started`;
  }
}

// `batchQueueEntries` moved to `use-batch-run.ts` with the run's start and stop (PKG-PM-C).
export { batchQueueEntries } from "@/components/grooming/use-batch-run";

/** Batch ids this page already asked the runner to start, so a remount never starts one twice. */
const startedBatches = new Set<string>();

/** The status mark at the start of a row (design `.st-*`). */
function StatusMark({ item }: { item: GroomingBatchItemView }) {
  const base = "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[11px] font-bold";
  if (item.status === "briefed" && item.reviewState === "posted") {
    return (
      <span className={cn(base, "bg-green-500 text-green-950")} aria-hidden="true" data-mark="posted">
        ✓
      </span>
    );
  }
  switch (item.status) {
    case "briefed":
    case "current":
      return (
        <span className={cn(base, "bg-green-500/15 text-green-400")} aria-hidden="true" data-mark="done">
          ✓
        </span>
      );
    case "running":
      return <span className={cn(base, "animate-spin border-2 border-primary border-r-transparent")} aria-hidden="true" data-mark="running" />;
    case "not-ready":
      return (
        <span className={cn(base, "bg-amber-500/15 text-amber-400")} aria-hidden="true" data-mark="not-ready">
          !
        </span>
      );
    case "failed":
      return (
        <span className={cn(base, "bg-primary/20 text-red-300")} aria-hidden="true" data-mark="failed">
          ×
        </span>
      );
    case "skipped":
      return (
        <span className={cn(base, "bg-secondary text-muted-foreground")} aria-hidden="true" data-mark="skipped">
          –
        </span>
      );
    case "queued":
      return <span className={cn(base, "border border-dashed border-zinc-600")} aria-hidden="true" data-mark="queued" />;
  }
}

/**
 * The short tag at the end of a row (design `runRows`), in the shared brief
 * words (UX-PM-09). `latest` is the item's latest brief from the cached
 * whole-backlog read (`useBacklogBriefs`); batch reads carry no staleness and
 * a `current` item no brief. The same `batchItemBriefState` as the Agenda's
 * `agendaState`: out of date wins, then posted or held back; a briefed item
 * not yet posted shows its points. On a finished run (`finished`) a queued
 * item is one added after it finished or whose brief was disregarded
 * (ER-1712): it says "Needs a brief", like the Session's prepared view.
 */
export function rowTag(
  item: GroomingBatchItemView,
  stopped: boolean,
  latest: Parameters<typeof batchItemBriefState>[1] = null,
  finished = false
): { text: string; tone: ChipTone | "points" } {
  switch (item.status) {
    case "briefed":
    case "current": {
      const state = batchItemBriefState(item, latest);
      if (state === "not-posted" && item.status === "briefed" && item.brief) {
        return { text: `${String(item.brief.suggestedPoints)} pts`, tone: "points" };
      }
      return briefStateTag(state);
    }
    case "running":
      return { text: "Analysing", tone: "red" };
    case "queued":
      if (finished) return { text: "Needs a brief", tone: "none" };
      return { text: stopped ? "Not started" : "Queued", tone: "none" };
    case "not-ready":
      return { text: "Not ready", tone: "warn" };
    case "failed":
      return { text: "Failed", tone: "red" };
    case "skipped":
      return { text: LEFT_OUT_TEXT, tone: "none" };
  }
}

/** Item statuses SpecTree already settled: the row keeps its server tag even if the queue still lists the item. */
const SETTLED: ReadonlySet<GroomingBatchItemStatus> = new Set(["briefed", "current", "not-ready", "failed", "skipped"]);

function RowTag({
  item,
  stopped,
  finished,
  queueState,
  project,
  latest,
}: {
  item: GroomingBatchItemView;
  stopped: boolean;
  finished: boolean;
  queueState: GroomingQueueState | null;
  project: string;
  latest: Parameters<typeof batchItemBriefState>[1];
}) {
  const queued = SETTLED.has(item.status) ? null : activeEntryByKey(queueState, safeQueueKey("groom-item", project, item.itemId));
  if (queued) return <QueueBadge status={queued} />;
  const tag = rowTag(item, stopped, latest, finished);
  if (tag.tone === "points") {
    return (
      <span
        className="inline-flex h-5 items-center whitespace-nowrap rounded-md border border-dashed border-zinc-600 px-1.5 text-[11px] font-semibold text-zinc-300"
        data-testid="run-row-tag"
      >
        {tag.text}
      </span>
    );
  }
  return (
    <Chip tone={tag.tone} testId="run-row-tag">
      {tag.text}
    </Chip>
  );
}

/**
 * The stop banner: usage limit, the person's own stop, or something to fix
 * first (UX-PM-07): what happened and one thing to do, in plain words and
 * local times, with Fix it for a setup problem and the code, engine message
 * and exact time behind "Copy details for the Admin".
 */
function StoppedBanner({
  project,
  batchId,
  stop,
  briefed,
  total,
  left,
  onResume,
  resuming,
  showFallback,
}: {
  project: string;
  batchId: string;
  stop: StopInfo;
  briefed: number;
  total: number;
  left: number;
  onResume: (() => void) | null;
  resuming: boolean;
  showFallback: boolean;
}) {
  const { support } = useRunnerBridge();
  const bridge = useToolboxBridge();
  const counts = `${String(briefed)} of ${String(total)} have a brief.`;
  const fixPath = groomingFixPath(stop.errorCode ?? (stop.reason === "environment" ? "environment" : null), project);
  let text: string;
  if (stop.reason === "usage-limit") {
    const reached = localTimeOf(stop.at);
    text = `Your Claude usage limit was reached${reached ? ` ${reached}` : ""}. ${counts} Nothing is lost. Resume once your limit resets.`;
    const resets = stop.resumeAt ? formatAboutTime(stop.resumeAt) : null;
    if (resets) text += ` It resets ${resets}.`;
  } else if (stop.reason === "user") {
    text = `${stopReasonMessage("user").cause} ${counts} Finished briefs stay ready to review and post.`;
  } else {
    const message = stop.errorCode ? groomingErrorMessage(stop.errorCode) : stopReasonMessage(stop.reason);
    text = `${message.cause} ${counts} ${message.whatToDo}`;
  }

  return (
    <div
      className="flex flex-col gap-2 border-b border-amber-500/25 bg-amber-500/[0.08] px-5 py-2.5 motion-safe:animate-in motion-safe:fade-in"
      role="status"
      data-testid="stopped-banner"
    >
      <div className="flex flex-wrap items-center gap-3">
        <Chip tone={stop.reason === "user" ? "none" : "warn"}>Stopped</Chip>
        <p className="min-w-0 flex-1 text-sm" data-testid="stopped-text">
          {text}
        </p>
        {fixPath && (
          <Link to={fixPath} className="text-sm text-link hover:underline" data-testid="first-launch-fix">
            Fix it
          </Link>
        )}
        {[stop.errorCode, stop.message].some(Boolean) && (
          <CopyButton
            label="Copy details for the Admin"
            text={adminDetailsText({
              what: "A session run stopped",
              project,
              runId: batchId,
              errorCode: stop.errorCode,
              rawMessage: stop.message,
              at: stop.at,
              appVersion: bridge?.app.version,
              contracts: bridge?.app.contracts,
            })}
          />
        )}
        {left > 0 && onResume && (
          <Button
            type="button"
            size="sm"
            className="border-primary bg-primary text-primary-foreground hover:bg-primary/90"
            disabled={resuming}
            onClick={onResume}
          >
            Resume ({left} left)
          </Button>
        )}
      </div>
      {left > 0 && !onResume && showFallback && <RunnerFallback support={support} />}
    </div>
  );
}

/** A waiting item's place in the run queue, when it has one. */
function SelectedQueueBadge({ project, itemId, state }: { project: string; itemId: string; state: GroomingQueueState | null }) {
  const active = activeEntryByKey(state, safeQueueKey("groom-item", project, itemId));
  return active ? <QueueBadge status={active} /> : null;
}

/** The selected item's title block for states without a brief. */
function ItemHeading({ item }: { item: GroomingBatchItemView }) {
  return (
    <div className="space-y-1.5">
      <p className="font-mono text-xs text-muted-foreground">AB#{item.itemId}</p>
      <h2 className="font-display text-[22px] font-semibold leading-tight tracking-[-0.01em]">{item.title}</h2>
    </div>
  );
}

/** The progress of the selected item, when the latest report is about it (or about the batch as a whole). */
function progressFor(progress: RunProgress | null, item: GroomingBatchItemView): RunProgress | null {
  if (!progress) return null;
  if (progress.batchItemId) return progress.batchItemId === item.id ? progress : null;
  if (progress.itemId) return progress.itemId === item.itemId ? progress : null;
  return progress;
}

type RunView = "workspace" | "agenda";

export interface BatchWorkspaceProps {
  project: string;
  batch: GroomingBatch;
  /** The back link to the build step, drawn first in the header. */
  back?: ReactNode;
  /** Extra header actions (the session page's Present), drawn after the view switch. */
  headerActions?: ReactNode;
  /**
   * The session's date (`YYYY-MM-DD`) for "Meeting of Thursday, Oct 15" and
   * "Briefs ready"; defaults to the batch's own session date.
   */
  sessionDate?: string | null;
}

/** The run step: header, banners, and the Workspace or Agenda view of one batch. */
export function BatchWorkspace({ project, batch, back, headerActions, sessionDate: sessionDateProp }: BatchWorkspaceProps) {
  const { runner, support } = useRunnerBridge();
  const supportsProgress = useSupportsRunProgress();
  const queryClient = useQueryClient();
  const { data: access } = useToolboxAccess();
  const canPrepare = canOn(access, project, "prepare-sessions");
  const canPostAll = canOn(access, project, "manage-agenda");
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState<RunView>("workspace");
  const [stopDetail, setStopDetail] = useState<StopInfo | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [postAllOpen, setPostAllOpen] = useState(false);
  const progress = useRunProgress(runner, { batchId: batch.id });
  // UX-PM-09: each brief's state from the cached whole-backlog read, never a per-row or new backlog read.
  const latestOf = useBacklogBriefs(project);
  // Start, Stop and Resume (run queue in a contract-3 app), shared with the Session screen's Prepare (PKG-PM-C).
  const batchRun = useBatchRun(project, batch, {
    onStart: () => {
      setStopDetail(null);
    },
  });
  const {
    start,
    stop: stopRun,
    busy,
    liveRun,
    setLiveRun,
    error: actionError,
    queueEntries: inQueue,
    queueMode,
    queueState,
  } = batchRun;
  const run = useCallback(() => {
    void start();
  }, [start]);

  // A link carrying autoRun (the build step before PKG-PM-C started runs in place) starts the run once.
  const autoRun = (location.state as { autoRun?: boolean } | null)?.autoRun === true;
  useEffect(() => {
    if (!autoRun || !runner || batch.status !== "draft" || startedBatches.has(batch.id)) return;
    startedBatches.add(batch.id);
    void navigate(location.pathname, { replace: true, state: null });
    run();
  }, [autoRun, runner, batch.status, batch.id, location.pathname, navigate, run]);

  const sessionDate = sessionDateProp ?? batch.sessionDate ?? null;
  const onEvent = useCallback(
    (event: GroomingRunnerEvent) => {
      if (!isBatchStateEvent(event, batch.id)) return;
      if (event.type === "item-started") setLiveRun(true);
      if (event.type === "batch-stopped") {
        setStopDetail({
          reason: event.reason,
          ...(event.errorCode ? { errorCode: event.errorCode } : {}),
          ...(event.message ? { message: event.message } : {}),
          ...(event.resumeAt ? { resumeAt: event.resumeAt } : {}),
          at: new Date().toISOString(),
        });
      }
      const applied = applyRunnerEventToCache(queryClient, batch.id, event);
      // A new brief replaces the backlog's latest one: pages that show the backlog read it again.
      if (event.type === "item-finished") refreshCachedBacklog(queryClient, project);
      if (event.type !== "batch-stopped" && event.type !== "batch-finished") return;
      // Settle only once the read after the event has landed: never on a copy from before it.
      void applied.then(() => {
        setLiveRun(false);
        // The rail's "ready to post" badge and the build step read the project's batches and session.
        void queryClient.invalidateQueries({ queryKey: groomingKeys.batches(project) });
        void queryClient.invalidateQueries({ queryKey: groomingKeys.session(project) });
        if (event.type === "batch-finished" && event.status === "completed" && event.briefed > 0) {
          void notifyBriefsReady({ count: event.briefed, project, sessionDate });
        }
      });
    },
    [batch.id, queryClient, project, sessionDate]
  );
  useRunnerEvents(runner, onEvent);

  const items = batch.items;
  const total = items.length;
  const done = items.filter((i) => FINISHED.has(i.status)).length;
  const briefed = items.filter((i) => i.status === "briefed" || i.status === "current").length;
  const queued = items.filter((i) => i.status === "queued").length;
  const held = heldBriefItems(items);
  const status = batch.status;
  const running = status === "running" || (liveRun && status === "draft");

  const stop: StopInfo | null =
    status === "stopped"
      ? (stopDetail ?? { reason: batch.stopReason ?? "error", at: batch.finishedAt ?? batch.startedAt ?? batch.createdAt })
      : null;

  const selected =
    items.find((i) => i.id === selectedId) ??
    items.find((i) => i.status === "briefed") ??
    items.find((i) => i.status === "running") ??
    items[0] ??
    null;

  // UX-PM-21 (PI-SPEC-SW-1457): picking another item starts its review at the top,
  // not at the previous item's scroll position.
  const reviewPaneRef = useRef<HTMLDivElement>(null);
  const selectedKey = selected?.id ?? null;
  useEffect(() => {
    if (reviewPaneRef.current) reviewPaneRef.current.scrollTop = 0;
  }, [selectedKey]);

  const canRun = canPrepare && runner !== null;
  const fill = total ? `${String(Math.round((done / total) * 100))}%` : "0%";
  const posted = postedText(batch);

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="batch-workspace">
      <div className="flex flex-wrap items-center gap-3 border-b border-[#1F1F23] pb-3">
        {back}
        <div className="flex w-[300px] max-w-full flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <h1 className="font-display text-sm font-semibold" data-testid="run-title">
              {meetingTitle(sessionDate)}
            </h1>
            {running && (
              <span
                className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-r-transparent"
                aria-hidden="true"
                data-testid="run-spinner"
              />
            )}
          </div>
          <div className="h-1 overflow-hidden rounded-full bg-[#1F1F23]" aria-hidden="true">
            <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: fill }} />
          </div>
          {posted && (
            <span className="text-xs text-muted-foreground" data-testid="run-posted">
              {posted}
            </span>
          )}
        </div>
        <p className="min-w-0 flex-1 text-xs text-muted-foreground" data-testid="run-progress">
          {runText(batch)}
        </p>
        <Segmented
          label="View"
          value={view}
          options={[
            { value: "workspace", label: "Workspace" },
            { value: "agenda", label: "Agenda" },
          ]}
          onChange={setView}
        />
        {headerActions}
        {(status === "running" || inQueue.running.length + inQueue.upNext.length > 0) && canRun && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => {
              void stopRun();
            }}
          >
            <Square className="h-3 w-3" aria-hidden="true" />
            Stop
          </Button>
        )}
        {status === "draft" && canRun && inQueue.running.length + inQueue.upNext.length === 0 && (
          <Button
            type="button"
            size="sm"
            className="border-primary bg-primary text-primary-foreground hover:bg-primary/90"
            disabled={busy || liveRun || total === 0}
            onClick={run}
          >
            Start the run
          </Button>
        )}
        {canPostAll && (
          <Button
            type="button"
            size="sm"
            className="border-primary bg-primary text-primary-foreground hover:bg-primary/90"
            disabled={held.length === 0}
            onClick={() => {
              setPostAllOpen(true);
            }}
          >
            Post all ({held.length})
          </Button>
        )}
      </div>

      {stop && (
        <StoppedBanner
          project={project}
          batchId={batch.id}
          stop={stop}
          briefed={briefed}
          total={total}
          left={queued}
          onResume={canRun ? run : null}
          resuming={busy || liveRun}
          showFallback={canPrepare}
        />
      )}
      {actionError && (
        <p className="flex items-center gap-2 px-1 pt-3 text-sm text-destructive" role="alert">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          {actionError}
        </p>
      )}
      {canPrepare && !runner && (status === "draft" || status === "running") && (
        <div className="pt-3">
          <RunnerFallback support={support} />
        </div>
      )}

      {view === "agenda" ? (
        <div className="min-h-0 flex-1 overflow-auto pt-4">
          <AgendaView
            project={project}
            batch={batch}
            onOpenItem={(batchItemId) => {
              setSelectedId(batchItemId);
              setView("workspace");
            }}
          />
        </div>
      ) : (
        <div className="grid min-h-[520px] flex-1 grid-cols-1 md:grid-cols-[360px_minmax(0,1fr)]">
          <ul
            className="min-h-0 overflow-auto border-b border-[#1F1F23] md:border-b-0 md:border-r"
            aria-label="Items in this run"
            data-testid="run-list"
          >
            {items.map((item) => {
              const isSelected = selected?.id === item.id;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    data-testid="run-row"
                    data-item-id={item.itemId}
                    data-status={item.status}
                    aria-current={isSelected ? "true" : undefined}
                    onClick={() => {
                      setSelectedId(item.id);
                    }}
                    className={cn(
                      "relative flex h-[54px] w-full items-center gap-2.5 border-b border-[#141417] px-3.5 text-left transition-colors hover:bg-[#0F0F12]",
                      isSelected && "bg-[#15151A] shadow-[inset_2px_0_0_var(--primary)]"
                    )}
                  >
                    <StatusMark item={item} />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-sm">{item.title}</span>
                      <span className="font-mono text-xs text-muted-foreground">AB#{item.itemId}</span>
                    </span>
                    <RowTag
                      item={item}
                      stopped={status === "stopped"}
                      finished={status === "completed"}
                      queueState={queueMode ? queueState : null}
                      project={project}
                      latest={latestOf(item.itemId)}
                    />
                  </button>
                </li>
              );
            })}
          </ul>

          <div ref={reviewPaneRef} className="min-w-0 overflow-auto px-7 py-5" data-testid="review-pane">
            {!selected ? (
              <p className="text-sm text-muted-foreground">This run has no items.</p>
            ) : selected.status === "briefed" || selected.status === "current" ? (
              <BriefReview
                key={selected.itemId}
                project={project}
                itemId={selected.itemId}
                batchId={batch.id}
                layout="workspace"
                enterToPost={canPostAll && !postAllOpen}
              />
            ) : (
              <div className="flex flex-col gap-4">
                <ItemHeading item={selected} />
                {selected.status === "failed" ? (
                  <FailedItemCard key={selected.itemId} project={project} batchId={batch.id} item={selected} />
                ) : selected.status === "not-ready" ? (
                  <NotReadyCard project={project} itemId={selected.itemId} why={selected.errorMessage} />
                ) : selected.status === "running" ? (
                  <RunActivity
                    key={selected.id}
                    progress={progressFor(progress, selected)}
                    startedAt={selected.startedAt}
                    typicalItemSeconds={batch.typicalItemSeconds ?? null}
                    supportsProgress={supportsProgress && runner !== null}
                    note="You can review and post finished items while this runs."
                  />
                ) : (
                  <div className="glass-card flex items-center gap-2.5 rounded-[10px] px-5 py-[18px]" data-testid="waiting-item">
                    <span className="h-[18px] w-[18px] shrink-0 rounded-full border border-dashed border-zinc-600" aria-hidden="true" />
                    <SelectedQueueBadge project={project} itemId={selected.itemId} state={queueMode ? queueState : null} />
                    <p className="text-sm text-muted-foreground">
                      {selected.status === "skipped"
                        ? `${LEFT_OUT_TEXT}. It has no new brief.`
                        : status === "stopped"
                          ? "Not started. Resume the run to brief it."
                          : status === "completed"
                            ? "Needs a brief. The next Prepare briefs run on the Session page briefs it."
                            : "Waiting for its turn."}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {canPostAll && <PostAllDialog project={project} batch={batch} open={postAllOpen} onOpenChange={setPostAllOpen} />}
    </div>
  );
}
